/**
 * `resolveKeymap` — layered keymap precedence (#64).
 *
 * Accounts don't exist yet, but the precedence is designed NOW so that when an
 * account tier arrives it is a pure data change, not a code change:
 *
 *   account  ⟶  browser  ⟶  defaults
 *
 * Resolution is PER-ACTION: for each action, the highest tier that binds it
 * wins; unset actions fall through to the next tier, and finally to defaults.
 * So a partial account keymap (only a couple of rebound actions) still inherits
 * browser/default bindings for everything it doesn't mention.
 *
 * Today only `browser` (localStorage) + `defaults` are ever passed; `account`
 * stays undefined until the account system (#65) lands. The function is fully
 * built + tested now so the later change is "start passing an account keymap",
 * nothing more.
 *
 * The result always covers exactly the action set present in `defaults` (the
 * canonical universe of actions) — an override tier can re-bind an action but
 * cannot introduce an action the defaults don't define.
 */

import type { GameAction } from "./actions.js";
import { gameActionId } from "./actions.js";
import type { Keymap, KeyBinding } from "./keymap.js";

/** Sources for resolution, highest precedence first when present. */
export interface KeymapSources {
  /** Per-account keymap (future — undefined/null until accounts exist). */
  readonly account?: Keymap | null | undefined;
  /** Per-browser keymap from localStorage (null if none/absent). */
  readonly browser?: Keymap | null | undefined;
  /** The canonical default keymap. Always present; defines the action set. */
  readonly defaults: Keymap;
}

/**
 * Resolve the effective keymap. Per-action precedence account → browser →
 * defaults. The action universe is fixed by `defaults`; override tiers can only
 * re-bind actions that exist in defaults (an override binding an unknown action
 * is ignored, keeping the result canonical). Pure.
 */
export function resolveKeymap(sources: KeymapSources): Keymap {
  const { account, browser, defaults } = sources;
  const overrideTiers: readonly (Keymap | null | undefined)[] = [account, browser];

  const bindings: KeyBinding[] = defaults.bindings.map((def) => {
    const override = firstBindingFor(def.action, overrideTiers);
    return override ?? def;
  });
  return { bindings };
}

/**
 * Find the highest-precedence override binding for `action`, or `undefined` if
 * no override tier binds it (caller falls back to the default binding).
 */
function firstBindingFor(
  action: GameAction,
  tiers: readonly (Keymap | null | undefined)[],
): KeyBinding | undefined {
  const wantId = gameActionId(action);
  for (const tier of tiers) {
    if (tier === null || tier === undefined) continue;
    for (const binding of tier.bindings) {
      if (gameActionId(binding.action) === wantId) return binding;
    }
  }
  return undefined;
}
